import { ArrowRight } from "lucide-react";

/**
 * The explainer that earns the "pull oracle" promise. Three numbered steps,
 * each with a short verb headline, a one-sentence description, and a small
 * code snippet — so the visitor sees the actual shape of integration without
 * having to navigate to the docs.
 *
 * No motion: three calm cards in a row, separated by quiet directional arrows.
 */
export function HowPullWorks() {
  return (
    <section
      className="relative z-10 mx-auto w-full max-w-7xl px-6 pt-24 pb-12 sm:px-10 sm:pt-32"
      aria-labelledby="how-title"
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-m45">
        <span className="text-emerald">●</span> HOW IT WORKS
      </p>
      <h2
        id="how-title"
        className="mt-6 font-serif text-3xl leading-[1.05] tracking-[-0.012em] text-text sm:text-5xl md:text-6xl"
      >
        Three steps. <em className="italic text-emerald">Ten lines.</em> One transaction.
      </h2>
      <p className="mt-5 max-w-2xl text-base leading-relaxed text-m70">
        Fetch a freshly signed price off-chain, bundle the verification op into your own
        transaction, and execute against state signed within the last ~500&nbsp;ms.
      </p>

      <div className="mt-12 grid grid-cols-1 gap-4 lg:grid-cols-[1fr_auto_1fr_auto_1fr] lg:items-stretch lg:gap-3">
        <Step
          n="01"
          verb="FETCH"
          headline="Pull a signed price"
          body="Your client calls the attestation service over HTTPS. It returns the latest signed message and Ed25519 signature within ~50 ms."
          code={`const fresh = await oracle\n  .fetchLatest(["BTC/USD"]);`}
        />
        <Arrow />
        <Step
          n="02"
          verb="BUNDLE"
          headline="Bundle the verification op"
          body="Add the verification op to the same transaction that carries your application logic. One trip, two intentions."
          code={`tx.addOperation(\n  fresh.toUpdateOp(ORACLE)\n);`}
        />
        <Arrow />
        <Step
          n="03"
          verb="EXECUTE"
          headline="Run against fresh state"
          body="Soroban verifies the signature, stores the price, then executes your application op against the just-verified value."
          code={`tx.addOperation(\n  perp.call("open_position")\n);`}
        />
      </div>
    </section>
  );
}

function Step({
  n,
  verb,
  headline,
  body,
  code,
}: {
  n: string;
  verb: string;
  headline: string;
  body: string;
  code: string;
}) {
  return (
    <article className="group flex h-full flex-col rounded-xl border border-border bg-surface/40 p-6 transition-colors hover:border-border-2 hover:bg-surface/60">
      <div className="flex items-baseline gap-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-emerald">
          {n}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-m45">
          {verb}
        </span>
      </div>
      <h3 className="mt-5 font-serif text-[22px] leading-tight text-text">
        {headline}
      </h3>
      <p className="mt-3 text-[14px] leading-relaxed text-m70">{body}</p>
      <pre className="mt-5 overflow-x-auto rounded-md border border-border bg-bg-deep/60 px-3.5 py-3 font-mono text-[11.5px] leading-relaxed text-text">
        <code>{code}</code>
      </pre>
    </article>
  );
}

function Arrow() {
  return (
    <div className="hidden lg:flex items-center justify-center text-m30">
      <ArrowRight className="size-4" />
    </div>
  );
}
